import type { DrawContext } from './draw';
import { toScreen } from './project';
import type { View } from './project';
import type { SkyField } from './field';
import { skyView } from './sky';
import type { LabelSpace } from './labels';
import { traditionsNaming } from './nodes';

/*
 * DRAWING TEN THOUSAND NAMES — by mostly not drawing them.
 *
 * Ported from `reference/universe-v8/engine.js` — the level-of-detail bands of `drawSky`
 * (1005–1112). Checked against it by comparing recorded draw calls.
 *
 * THIS IS THE ANSWER OUR RENDERER HAD NO VERSION OF. Ours drew every figure the same way at every
 * zoom and simply drew fewer of them as you closed in. Theirs draws a DIFFERENT PICTURE in each of
 * four bands, and the two furthest out never touch an individual name at all:
 *
 *   3  clumps      one disc per clump cell, sized by the square root of its population
 *   2  dots        every name a 1.3–1.7px rect, batched into ONE path per corpus
 *   1  discs       real circles, no rings
 *   0  full        rings, and labels for the few names worth one
 *
 * Band 2 is the one worth staring at. Their comment — *"four fills for the whole sky, whatever its
 * size"* — is the whole trick: ten thousand rects go into four paths, so the cost of the field
 * stops depending on how many names are in it. Our old renderer's equivalent layer was ~5,560
 * separate fills a frame.
 *
 * All four bands are here. The label placement they end in is in `labels.ts`.
 */

/**
 * How much of the sky is drawn and how, all of it DATA — none of these describe their method.
 *
 * `corpusKeys` and `palette` are four entries in their build and eight in ours, and every loop that
 * walked `q < 4` in the reference walks these instead.
 */
export interface SkyPaint {
  corpusKeys: readonly string[];
  /** Corpus key → colour. */
  palette: Readonly<Record<string, string>>;
  /** World units per grid cell and per clump cell — the same values the field was bucketed with. */
  gridCell: number;
  clumpCell: number;
  squash: number;
  /** Corpus keys the reader has switched off. */
  hidden?: ReadonlySet<string>;
  /**
   * How many texts must name a figure before it is drawn OPEN rather than in one text's colour.
   *
   * DATA. Theirs is three, over a four-corpus roster where three of four is nearly all of them.
   * Ours carries eight texts held by three traditions, so the same number would mean something
   * quite different — which is the shape of mistake the first port recorded as deviation 7.
   */
  sharedAtLeast: number;
  traditionsOfKey: Readonly<Record<string, readonly string[]>>;
  /** Whether anything is dimmed at all: 1 while the arrival is showing the whole field. */
  reveal?: boolean;
}

/** What a band drew, for the cost readout their `perf` keeps. */
export interface SkyCounts {
  cells: number;
  dots: number;
  discs: number;
  clumps: number;
}

/** The colour a name falls back to when no corpus claims it. */
const UNCLAIMED = '#c9d2e4';

/**
 * How much dimmer the streamed field is than anything walked.
 *
 * Their comment: *"sky names are scenery: dimmer than anything you have walked"*. Full strength
 * only while the arrival is deliberately showing the whole field.
 */
function fogOf(paint: SkyPaint): number {
  return paint.reveal ? 1 : 0.5;
}

/** Which corpora a name belongs to, from its packed mask. */
export function corporaOf(mask: number, corpusKeys: readonly string[]): string[] {
  const out: string[] = [];
  for (let q = 0; q < corpusKeys.length; q++) {
    const key = corpusKeys[q];
    if (key !== undefined && (mask & (1 << q))) out.push(key);
  }
  return out;
}

/** Whether a name survives the reader's corpus filter. A name no corpus claims is always shown. */
export function visibleUnderFilter(mask: number, paint: SkyPaint): boolean {
  if (!mask) return true;
  const hidden = paint.hidden;
  if (!hidden) return true;
  for (let q = 0; q < paint.corpusKeys.length; q++) {
    const key = paint.corpusKeys[q];
    if (key !== undefined && (mask & (1 << q)) && !hidden.has(key)) return true;
  }
  return false;
}

/** The cell range a viewport covers, at a given cell size. */
function cellRange(v: { x0: number; x1: number; y0: number; y1: number }, cell: number) {
  return { c0: (v.x0 / cell) | 0, c1: (v.x1 / cell) | 0, r0: (v.y0 / cell) | 0, r1: (v.y1 / cell) | 0 };
}

/**
 * The buckets a band walks: the ones the viewport covers, or every one there is.
 *
 * `cull` is the profiler's switch — a frame drawn without it prices what the culling is saving,
 * which is the only honest way to know. It is theirs (`cullOffscreen`), and it changes the ORDER
 * as well as the count: the culled path walks the grid in column-major order and the uncalled one
 * walks the map in insertion order, so a comparison notices even where the same names are drawn.
 */
function buckets(
  cells: ReadonlyMap<string, number[]>,
  v: { x0: number; x1: number; y0: number; y1: number },
  cell: number,
  cull: boolean,
): number[][] {
  if (!cull) return [...cells.values()];
  const { c0, c1, r0, r1 } = cellRange(v, cell);
  const found: number[][] = [];
  for (let cx = c0; cx <= c1; cx++) {
    for (let cy = r0; cy <= r1; cy++) {
      const bucket = cells.get(`${cx},${cy}`);
      if (bucket) found.push(bucket);
    }
  }
  return found;
}

/** How far past the edge of the screen a thing is still drawn, so nothing pops in at the border. */
const CLUMP_MARGIN = 30;
const DOT_MARGIN = 2;

/** Their view padding for the draw — a hair wider than the screen. */
export const DRAW_PAD = 1.04;

/**
 * BAND 3 — the furthest out: one disc per clump, and not a single name touched.
 *
 * Pulled right back, a name is a fraction of a pixel and ten thousand of them are a grey smear. So
 * the field is drawn as its own density: each clump cell gets one disc at the mean position of
 * whatever is in it, sized by the SQUARE ROOT of its population — because a clump is read as an
 * area, so four times the names should read as twice the mark.
 */
export function drawSkyClumps(
  ctx: DrawContext,
  field: SkyField,
  view: View,
  paint: SkyPaint,
  counts: SkyCounts,
): void {
  const v = skyView(view.cam, view.view, paint.squash, DRAW_PAD);
  const { c0, c1, r0, r1 } = cellRange(v, paint.clumpCell);
  const fog = fogOf(paint);
  const W = view.view.width;
  const H = view.view.height;

  for (let cx = c0; cx <= c1; cx++) {
    for (let cy = r0; cy <= r1; cy++) {
      const a = field.clumps.get(`${cx},${cy}`);
      if (!a) continue;
      counts.cells++;
      const mx = a.x / a.n;
      const my = a.y / a.n;
      const p = toScreen(view, mx, my * paint.squash);
      if (p.x < -CLUMP_MARGIN || p.x > W + CLUMP_MARGIN || p.y < -CLUMP_MARGIN || p.y > H + CLUMP_MARGIN) continue;
      /* The corpus with most names here decides the colour. Ties go to the earlier key, as theirs. */
      let dom = 0;
      let best = -1;
      for (let q = 0; q < paint.corpusKeys.length; q++) {
        const n = a.c[q] ?? 0;
        if (n > best) { best = n; dom = q; }
      }
      ctx.globalAlpha = Math.min(0.6, 0.18 + a.n * 0.0035) * fog;
      ctx.fillStyle = paint.palette[paint.corpusKeys[dom] ?? ''] ?? UNCLAIMED;
      ctx.beginPath();
      ctx.arc(p.x, p.y, Math.max(1.1, Math.min(14, Math.sqrt(a.n) * 0.66)), 0, 6.2832);
      ctx.fill();
      counts.clumps++;
    }
  }
  ctx.globalAlpha = 1;
}

/**
 * BAND 2 — FOUR FILLS FOR THE WHOLE SKY, WHATEVER ITS SIZE.
 *
 * Their comment, and the single most important line in the drawing half. Every visible name becomes
 * a rect a pixel or two across, and every rect of one corpus goes into ONE path — so the field
 * costs one fill per corpus rather than one per name, and ten thousand names cost exactly what a
 * hundred do.
 *
 * (One fill per corpus, so "four" is their roster's number. Ours is eight, and the point is that it
 * is the count of TEXTS and never the count of names.)
 */
export function drawSkyDots(
  ctx: DrawContext,
  field: SkyField,
  view: View,
  paint: SkyPaint,
  counts: SkyCounts,
  cull = true,
): number {
  const v = skyView(view.cam, view.view, paint.squash, DRAW_PAD);
  const fog = fogOf(paint);
  const W = view.view.width;
  const H = view.view.height;

  /* Gathered per corpus first, drawn per corpus after — the batching IS the mechanism. */
  const byCorpus = new Map<string, number[]>();
  for (const key of paint.corpusKeys) byCorpus.set(key, []);

  for (const bucket of buckets(field.cells, v, paint.gridCell, cull)) {
    {
      counts.cells++;
      for (const i of bucket) {
        if (!visibleUnderFilter(field.cs[i] ?? 0, paint)) continue;
        const p = toScreen(view, field.x[i] ?? 0, field.y[i] ?? 0);
        if (p.x < -DOT_MARGIN || p.x > W + DOT_MARGIN || p.y < -DOT_MARGIN || p.y > H + DOT_MARGIN) continue;
        const key = paint.corpusKeys[((field.dom[i] ?? 1) - 1)];
        if (key === undefined) continue;
        byCorpus.get(key)?.push(p.x, p.y);
        counts.dots++;
      }
    }
  }

  const size = view.cam.z > 0.12 ? 1.7 : 1.3;
  for (const key of paint.corpusKeys) {
    const arr = byCorpus.get(key);
    if (!arr || !arr.length) continue;
    ctx.fillStyle = paint.palette[key] ?? UNCLAIMED;
    ctx.globalAlpha = 0.5 * fog + 0.12;
    ctx.beginPath();
    for (let q = 0; q < arr.length; q += 2) ctx.rect(arr[q] ?? 0, arr[q + 1] ?? 0, size, size);
    ctx.fill();
  }
  ctx.globalAlpha = 1;
  return counts.dots;
}

/** A fresh set of counters, so a caller never has to remember the field names. */
export function skyCounts(): SkyCounts {
  return { cells: 0, dots: 0, discs: 0, clumps: 0 };
}

/** The dark core a figure every tradition names is drawn with, and the rim that keeps it readable. */
const APERTURE_CORE = '#06070f';
const APERTURE_RIM = 'rgba(222,234,255,.45)';
/** A name no corpus claims. */
const UNCLAIMED_DISC = '#c3ccdf';
/** Sky names are written faintly: they are scenery that happens to be legible, not labels. */
const SKY_LABEL = 'rgba(226,234,250,.34)';
const SKY_LABEL_FONT = '400 12px Spectral, Georgia, serif';
/** Straight up, where a ring of corpus arcs begins. */
const TOP = -1.5708;

/** At most this many sky names are written in a frame, however many qualify. */
const SKY_LABEL_CAP = 10;

/** One name that earned a label, kept until they can be ranked against each other. */
interface LabelCandidate {
  i: number;
  sx: number;
  sy: number;
  r: number;
}

/**
 * BANDS 1 AND 0 — real discs, and at the closest band their rings and a few names.
 *
 * Everything cheap has already been decided by the time this runs: the tile window chose which
 * names exist, the LOD chose that they are worth a disc at all. What is left is the look.
 *
 * `full` is band 0. It adds the corpus ring — one arc per text naming the figure, so a glance says
 * WHICH scriptures rather than only how many — and it is the only band that writes a name.
 */
export function drawSkyDiscs(
  ctx: DrawContext,
  field: SkyField,
  view: View,
  paint: SkyPaint,
  counts: SkyCounts,
  full: boolean,
  labels: LabelSpace,
  cull = true,
): void {
  const v = skyView(view.cam, view.view, paint.squash, DRAW_PAD);
  const fog = fogOf(paint);
  const W = view.view.width;
  const H = view.view.height;
  const z = view.cam.z;
  const candidates: LabelCandidate[] = [];

  for (const bucket of buckets(field.cells, v, paint.gridCell, cull)) {
    {
      counts.cells++;
      for (const i of bucket) {
        const mask = field.cs[i] ?? 0;
        if (!visibleUnderFilter(mask, paint)) continue;
        const p = toScreen(view, field.x[i] ?? 0, field.y[i] ?? 0);
        const r = Math.max(0.9, Math.min(46, (field.r[i] ?? 0) * z));
        if (p.x < -r - 24 || p.x > W + r + 24 || p.y < -r - 24 || p.y > H + r + 24) continue;

        const named = corporaOf(mask, paint.corpusKeys);
        /*
         * A figure named across enough texts is drawn OPEN — a dark core reading as a way through
         * rather than a disc, because its colour would otherwise claim one scripture names it most.
         * The threshold is DATA: theirs is three of four corpora, and ours is neither number.
         */
        const shared = traditionsNaming(named, paint.traditionsOfKey) >= paint.sharedAtLeast;
        ctx.globalAlpha = (0.5 + 0.3 * fog) * 0.72;
        ctx.fillStyle = shared
          ? APERTURE_CORE
          : paint.palette[paint.corpusKeys[(field.dom[i] ?? 1) - 1] ?? ''] ?? UNCLAIMED_DISC;
        ctx.beginPath();
        ctx.arc(p.x, p.y, r * 0.92, 0, 6.2832);
        ctx.fill();
        if (shared && r > 2.4) {
          ctx.lineWidth = Math.max(1, r * 0.1);
          ctx.strokeStyle = APERTURE_RIM;
          ctx.stroke();
        }

        if (full && named.length > 1 && r >= 3.4) {
          const rr = r + Math.max(2.6, r * 0.42);
          const lw = Math.max(1.2, Math.min(3.2, r * 0.2));
          const step = 6.2832 / named.length;
          /* A gap between arcs, so a ring of two does not read as one circle. */
          const gap = Math.min(0.3, 1.05 / named.length);
          ctx.lineWidth = lw;
          named.forEach((key, q) => {
            ctx.strokeStyle = paint.palette[key] ?? UNCLAIMED_DISC;
            ctx.beginPath();
            ctx.arc(p.x, p.y, rr, q * step + TOP + gap * 0.5, (q + 1) * step + TOP - gap * 0.5);
            ctx.stroke();
          });
        }
        counts.discs++;
        if (full && z > 1.5 && r > 3) candidates.push({ i, sx: p.x, sy: p.y, r });
      }
    }
  }
  ctx.globalAlpha = 1;

  /*
   * A SKY NAME EARNS A LABEL ONLY WHEN THE READER IS RIGHT ON TOP OF IT, and never over a real one.
   *
   * Ranked by weight so the ones worth reading get first refusal, then greedily placed: off the
   * edge, under the interface, or overlapping something already written all mean skipped rather
   * than moved. Ten at most, because eleven is a wall of text over a field.
   */
  if (!candidates.length) return;
  candidates.sort((a, b) => (field.w[b.i] ?? 0) - (field.w[a.i] ?? 0));
  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';
  ctx.font = SKY_LABEL_FONT;
  let placed = 0;
  for (const label of candidates) {
    if (placed >= SKY_LABEL_CAP) break;
    const name = field.name[label.i] ?? '';
    const lx = label.sx + label.r + 6;
    const w = ctx.measureText(name).width;
    if (lx + w > W - 10 || lx < 10) continue;
    if (labels.blocked({ x0: lx, y0: label.sy - 8, x1: lx + w, y1: label.sy + 8 })) continue;
    if (!labels.claim({ x0: lx, y0: label.sy - 9, x1: lx + w, y1: label.sy + 9 })) continue;
    ctx.fillStyle = SKY_LABEL;
    ctx.fillText(name, lx, label.sy + 0.5);
    placed++;
  }
}
